import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Globe, Check, Phone, MessageCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api from '../lib/api';
import { getWhatsAppLink, WHATSAPP_NUMBER } from '../lib/utils';
import SuccessModal from '../components/SuccessModal';

const popularDestinations = [
  "Japon", "Australie", "Brésil", "Turquie", "Thaïlande",
  "Royaume-Uni", "Corée du Sud", "Mexique", "Égypte", "Maroc"
];

export const VisaOtherPage = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', destination: '', description: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/contact', {
        name: formData.name, email: formData.email, phone: formData.phone,
        subject: `Demande de visa - ${formData.destination}`,
        message: formData.description
      });
      setFormData({ name: '', email: '', phone: '', destination: '', description: '' });
      setShowSuccess(true);
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#020617] min-h-screen overflow-x-hidden">
      {/* Header */}
      <section className="pt-6 pb-4 relative">
        <div className="absolute inset-0 gradient-radial opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 relative">
          <button onClick={() => navigate('/visa')}
            className="flex items-center gap-2 text-[#94A3B8] hover:text-[#D4AF37] transition-colors mb-4"
            data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
              <Globe className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h1 className="text-xl md:text-3xl font-bold text-white">Autres Destinations</h1>
              <p className="text-[#94A3B8] text-xs">Demande personnalisée pour toute destination</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content - Single Column */}
      <section className="py-4">
        <div className="max-w-xl mx-auto px-4 sm:px-6 md:px-12 flex flex-col gap-6">
          {/* Destinations */}
          <motion.div className="card-luxury p-4" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <h3 className="text-sm font-semibold text-white mb-3">Destinations Populaires</h3>
            <div className="grid grid-cols-2 gap-2">
              {popularDestinations.map((dest, i) => (
                <div key={i} className="flex items-center gap-2 p-1.5 rounded-lg bg-white/5 border border-white/5">
                  <Check className="w-3 h-3 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#94A3B8] text-xs">{dest}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Contact Direct */}
          <motion.div className="card-luxury p-4" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <h3 className="text-sm font-semibold text-white mb-2">Contact Direct</h3>
            <p className="text-[#94A3B8] text-xs mb-3">Pour une réponse rapide</p>
            <div className="grid grid-cols-2 gap-3">
              <a href="tel:+243975608422" className="flex flex-col items-center p-3 rounded-xl bg-white/5 border border-white/10 hover:border-[#D4AF37]/30 transition-colors" data-testid="call-btn">
                <Phone className="w-5 h-5 text-[#D4AF37] mb-1" />
                <span className="font-medium text-white text-xs">Appeler</span>
              </a>
              <a href={getWhatsAppLink(WHATSAPP_NUMBER, 'Bonjour, je souhaite un visa pour une autre destination.')} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center p-3 rounded-xl bg-white/5 border border-white/10 hover:border-green-500/30 transition-colors" data-testid="whatsapp-btn">
                <MessageCircle className="w-5 h-5 text-green-400 mb-1" />
                <span className="font-medium text-white text-xs">WhatsApp</span>
              </a>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div className="card-luxury p-4" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <h3 className="text-sm font-semibold text-white mb-3">Formulaire de Demande</h3>
            <form onSubmit={handleSubmit} className="space-y-3" data-testid="other-visa-form">
              <div>
                <Label className="text-[#94A3B8] text-xs">Nom Complet *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required placeholder="Votre nom complet" className="bg-white/5 border-white/10 text-white text-xs h-9" data-testid="input-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#94A3B8] text-xs">Email *</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required placeholder="votre@email.com" className="bg-white/5 border-white/10 text-white text-xs h-9" data-testid="input-email" />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">Téléphone *</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required placeholder="+243..." className="bg-white/5 border-white/10 text-white text-xs h-9" data-testid="input-phone" />
                </div>
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs">Destination *</Label>
                <Input value={formData.destination} onChange={(e) => setFormData({...formData, destination: e.target.value})}
                  required placeholder="Ex: Japon, Australie..." className="bg-white/5 border-white/10 text-white text-xs h-9" data-testid="input-destination" />
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs">Description *</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required placeholder="Décrivez votre projet de voyage..." rows={3}
                  className="bg-white/5 border-white/10 text-white text-xs resize-none" data-testid="input-description" />
              </div>
              <button type="submit" className="w-full btn-gold py-3 text-xs" disabled={submitting} data-testid="submit-btn">
                {submitting ? 'Envoi...' : 'Envoyer la Demande'}
              </button>
            </form>
          </motion.div>

          {/* Info */}
          <motion.div className="card-luxury p-4 bg-gradient-to-br from-amber-500/10 to-transparent border-amber-500/20" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <h4 className="font-semibold text-amber-400 mb-1 text-xs">Notre Engagement</h4>
            <p className="text-[#94A3B8] text-xs">Quelle que soit votre destination, notre équipe vous accompagne. Taux de réussite de 98% depuis 2011.</p>
          </motion.div>
        </div>
      </section>

      <SuccessModal 
        isOpen={showSuccess} 
        onClose={() => setShowSuccess(false)}
        title="Demande envoyée !"
        message="Votre demande de visa a été transmise avec succès. Notre équipe vous contactera sous peu pour étudier votre dossier."
      />
    </div>
  );
};

export default VisaOtherPage;
